import { useState, useCallback, useMemo } from "react";

/**
 * Custom hook for pagination
 * @param {Object} options - Pagination options
 * @param {number} options.initialPage - Initial page number (default: 1)
 * @param {number} options.initialItemsPerPage - Initial items per page (default: 50)
 * @param {number} options.totalItems - Total number of items
 * @returns {Object} Pagination state and methods
 */
export const usePagination = ({
    initialPage = 1,
    initialItemsPerPage = 50,
    totalItems = 0
}) => {
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

    const totalPages = useMemo(
        () => Math.ceil(totalItems / itemsPerPage) || 1,
        [totalItems, itemsPerPage]
    );

    const startIndex = useMemo(
        () => Math.max(0, (currentPage - 1) * itemsPerPage),
        [currentPage, itemsPerPage]
    );

    const endIndex = useMemo(
        () => Math.min(startIndex + itemsPerPage, totalItems),
        [startIndex, itemsPerPage, totalItems]
    );

    const goToPage = useCallback((page) => {
        const pageNumber = Math.max(1, Math.min(page, totalPages));
        setCurrentPage(pageNumber);
    }, [totalPages]);

    const nextPage = useCallback(() => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    }, [currentPage, totalPages]);

    const previousPage = useCallback(() => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    }, [currentPage]);

    const changeItemsPerPage = useCallback((newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    }, []);

    const reset = useCallback(() => {
        setCurrentPage(initialPage);
        setItemsPerPage(initialItemsPerPage);
    }, [initialPage, initialItemsPerPage]);

    return {
        currentPage,
        itemsPerPage,
        totalPages,
        totalItems,
        startIndex,
        endIndex,
        goToPage,
        nextPage,
        previousPage,
        changeItemsPerPage,
        setCurrentPage,
        setItemsPerPage,
        reset
    };
};

